/**
 * Durata delle visite: motore deterministico e configurabile.
 *
 * Questi valori NON sono verita' assolute. L'interfaccia li presenta sempre come
 * «durata consigliata» o «durata stimata» e l'utente puo' modificarli sulla
 * singola tappa. In futuro le durate realmente registrate potranno correggere
 * le medie: la tabella e' gia' pensata per essere sovrascritta dai dati.
 */

import type { DataStatus, PlaceCategory, TravelPace } from '../domain.js';
import { PACE_PROFILES } from '../domain.js';

export interface VisitDurationRule {
  readonly category: PlaceCategory;
  readonly minMinutes: number;
  readonly maxMinutes: number;
  readonly defaultMinutes: number;
}

/** Fallback usato se il database non ha ancora le regole (o non e' raggiungibile). */
export const DEFAULT_VISIT_RULES: Record<PlaceCategory, VisitDurationRule> = {
  viewpoint: { category: 'viewpoint', minMinutes: 20, maxMinutes: 45, defaultMinutes: 30 },
  monument: { category: 'monument', minMinutes: 30, maxMinutes: 60, defaultMinutes: 45 },
  monument_large: { category: 'monument_large', minMinutes: 60, maxMinutes: 150, defaultMinutes: 90 },
  church: { category: 'church', minMinutes: 30, maxMinutes: 60, defaultMinutes: 40 },
  museum_small: { category: 'museum_small', minMinutes: 60, maxMinutes: 90, defaultMinutes: 75 },
  museum_large: { category: 'museum_large', minMinutes: 120, maxMinutes: 240, defaultMinutes: 180 },
  gallery: { category: 'gallery', minMinutes: 60, maxMinutes: 120, defaultMinutes: 90 },
  castle: { category: 'castle', minMinutes: 60, maxMinutes: 150, defaultMinutes: 90 },
  archaeological: { category: 'archaeological', minMinutes: 60, maxMinutes: 180, defaultMinutes: 120 },
  park: { category: 'park', minMinutes: 60, maxMinutes: 150, defaultMinutes: 90 },
  garden: { category: 'garden', minMinutes: 45, maxMinutes: 120, defaultMinutes: 75 },
  nature_reserve: { category: 'nature_reserve', minMinutes: 90, maxMinutes: 300, defaultMinutes: 150 },
  beach: { category: 'beach', minMinutes: 60, maxMinutes: 300, defaultMinutes: 120 },
  waterfall: { category: 'waterfall', minMinutes: 30, maxMinutes: 90, defaultMinutes: 45 },
  cave: { category: 'cave', minMinutes: 45, maxMinutes: 120, defaultMinutes: 75 },
  peak: { category: 'peak', minMinutes: 60, maxMinutes: 240, defaultMinutes: 120 },
  hiking: { category: 'hiking', minMinutes: 60, maxMinutes: 480, defaultMinutes: 150 },
  zoo: { category: 'zoo', minMinutes: 180, maxMinutes: 300, defaultMinutes: 240 },
  theme_park: { category: 'theme_park', minMinutes: 300, maxMinutes: 600, defaultMinutes: 480 },
  historic_district: { category: 'historic_district', minMinutes: 60, maxMinutes: 180, defaultMinutes: 120 },
  market: { category: 'market', minMinutes: 45, maxMinutes: 120, defaultMinutes: 75 },
  square: { category: 'square', minMinutes: 20, maxMinutes: 60, defaultMinutes: 30 },
  attraction: { category: 'attraction', minMinutes: 45, maxMinutes: 90, defaultMinutes: 60 },
  restaurant: { category: 'restaurant', minMinutes: 45, maxMinutes: 120, defaultMinutes: 75 },
  cafe: { category: 'cafe', minMinutes: 20, maxMinutes: 60, defaultMinutes: 30 },
  bar: { category: 'bar', minMinutes: 45, maxMinutes: 120, defaultMinutes: 60 },
  shopping: { category: 'shopping', minMinutes: 45, maxMinutes: 150, defaultMinutes: 75 },
  experience: { category: 'experience', minMinutes: 60, maxMinutes: 180, defaultMinutes: 90 },
};

export interface VisitDurationEstimate {
  readonly minutes: number;
  readonly minMinutes: number;
  readonly maxMinutes: number;
  readonly status: DataStatus;
  readonly label: string;
}

/**
 * Durata suggerita per una visita.
 *
 * Se il POI porta una durata propria (da fonte), quella e' `verified` e vince.
 * Altrimenti si applica la regola di categoria, marcata `estimated`, corretta dal
 * ritmo scelto dall'utente.
 */
export function estimateVisitDuration(params: {
  category: PlaceCategory;
  pace: TravelPace;
  knownMinutes?: number | null;
  knownStatus?: DataStatus;
  rules?: Partial<Record<PlaceCategory, VisitDurationRule>>;
  /** Per i percorsi escursionistici: lunghezza reale del tracciato. */
  trailLengthM?: number | null;
}): VisitDurationEstimate {
  const rule = params.rules?.[params.category] ?? DEFAULT_VISIT_RULES[params.category];

  if (params.knownMinutes !== null && params.knownMinutes !== undefined && params.knownMinutes > 0) {
    return {
      minutes: params.knownMinutes,
      minMinutes: rule.minMinutes,
      maxMinutes: rule.maxMinutes,
      status: params.knownStatus ?? 'verified',
      label: 'Durata indicata',
    };
  }

  // Trekking: se conosciamo il tracciato, il tempo si calcola invece di indovinarlo.
  // Regola di Naismith semplificata: ~4 km/h in piano, senza dislivello noto.
  if (params.category === 'hiking' && params.trailLengthM !== null && params.trailLengthM !== undefined) {
    const minutes = Math.max(30, Math.round(((params.trailLengthM / 1000 / 4) * 60) / 5) * 5);
    return {
      minutes,
      minMinutes: rule.minMinutes,
      maxMinutes: rule.maxMinutes,
      status: 'estimated',
      label: 'Durata stimata dal percorso',
    };
  }

  const factor = PACE_PROFILES[params.pace].visitFactor;
  const adjusted = Math.round((rule.defaultMinutes * factor) / 5) * 5;
  const minutes = Math.min(rule.maxMinutes, Math.max(rule.minMinutes, adjusted));

  return {
    minutes,
    minMinutes: rule.minMinutes,
    maxMinutes: rule.maxMinutes,
    status: 'estimated',
    label: 'Durata consigliata',
  };
}

export function formatVisitDuration(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return `${String(hours)}h`;
  return `${String(hours)}h${String(rest).padStart(2, '0')}`;
}
